import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { APP_CONSTANTS, ROUTE_NAMES } from '../constants';
import './HeroSection.css';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGetQuote = () => {
    navigate(ROUTE_NAMES.quote);
  };

  // Use responsive image sizes for mobile optimization
  const getHeroImageSrc = () => {
    if (isMobile) {
      return "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=75";
    }
    return "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80";
  };

  return (
    <section id="home" className="hero-section">
      <div className="hero-background">
        <img
          src={getHeroImageSrc()}
          alt="Professional home cleaning service in Australia - TaskWombat cleaners providing premium cleaning services"
          className="hero-image"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          width="1600"
          height="900"
        />
      </div>

      <div className="hero-overlay" />

      {/* Reduce animated circles on mobile for better performance */}
      {!isMobile && (
        <div className="hero-animated-circles">
          {[...Array(4)].map((_, index) => (
            <motion.div
              key={index}
              className={`animated-circle circle-${index}`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.15 }}
              transition={{
                duration: 1.5,
                delay: index * 0.2,
                repeat: Infinity,
                repeatType: 'reverse',
                repeatDelay: 1,
              }}
            />
          ))}
        </div>
      )}

      <div className="hero-content">
        {/* Upper Section: Content */}
        <div className="hero-upper-section">
          <div className="hero-left-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.4 : 0.8 }}
              className="hero-title"
            >
              TaskWombat: Commercial & Residential Cleaning Services Across Australia
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0.1 : 0.2 }}
              className="hero-subtitle"
            >
              Professional commercial & contract cleaning for offices, strata, facilities & hospitality venues. Plus residential cleaning services. Fully insured with $10M public liability. Long-term contracts available.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0.15 : 0.25 }}
              className="hero-services-highlight"
            >
              <ServiceHighlight icon="business" text="Commercial Cleaning" color="#2E9BF5" />
              <ServiceHighlight icon="home" text="Residential Cleaning" color="#2E9BF5" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0.2 : 0.3 }}
              className="hero-trust-badges"
            >
              <TrustBadge icon="shield" text="$10M Insured" />
              <TrustBadge icon="verified_user" text="Police Checked Staff" />
              <TrustBadge icon="assignment" text="Long-Term Contracts" />
              {!isMobile && <TrustBadge icon="verified" text="OH&S Compliant" />}
              {!isMobile && <TrustBadge icon="schedule" text="Scheduled Service" />}
            </motion.div>
          </div>
        </div>

        {/* Lower Section: Buttons and Stat Cards */}
        <div className="hero-lower-section">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0.3 : 0.4 }}
            className="hero-actions"
          >
            <button
              className="hero-btn hero-btn-primary"
              onClick={handleGetQuote}
            >
              <span className="material-icons">description</span>
              Get Instant Quote
            </button>
            <motion.button
              className="hero-btn hero-btn-secondary"
              onClick={() => navigate(ROUTE_NAMES.contact)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="material-icons">location_on</span>
              Schedule a Site Inspection
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: isMobile ? 0.4 : 0.8, delay: isMobile ? 0.35 : 0.5 }}
            className="hero-stats"
          >
            <StatItem 
              icon="shield" 
              number="$10M" 
              label="Public Liability Insurance" 
            />
            <StatItem 
              icon="verified_user" 
              number="100%" 
              label="Police Checked Staff" 
            />
            <StatItem 
              icon="assignment" 
              number="Long-Term" 
              label="Contract Cleaning" 
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

interface TrustBadgeProps {
  icon: string;
  text: string;
}

const TrustBadge: React.FC<TrustBadgeProps> = ({ icon, text }) => (
  <div className="trust-badge">
    <span className="material-icons">{icon}</span>
    <span>{text}</span>
  </div>
);

interface ServiceHighlightProps {
  icon: string;
  text: string;
  color: string;
}

const ServiceHighlight: React.FC<ServiceHighlightProps> = ({ icon, text, color }) => (
  <motion.div 
    className="service-highlight"
    style={{ borderColor: color }}
    whileHover={{ scale: 1.05, y: -3 }}
    transition={{ duration: 0.2 }}
  >
    <span className="material-icons" style={{ color: 'white' }}>{icon}</span>
    <span style={{ color: 'white' }}>{text}</span>
  </motion.div>
);

interface StatItemProps {
  icon?: string;
  number: string;
  label: string;
}

const StatItem: React.FC<StatItemProps> = ({ icon, number, label }) => (
  <motion.div 
    className="stat-item"
    whileHover={{ scale: 1.05, y: -5 }}
    transition={{ duration: 0.3 }}
  >
    <div className="stat-content">
      {icon && <span className="material-icons stat-icon">{icon}</span>}
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  </motion.div>
);

export default HeroSection;
